/* 0.46.77 - Buttons and focus (0.47.11: Settings > Design system).
 *
 * The admin's values become CSS custom properties on the page root.
 * Button.module.css reads each one with the Henaket value as its
 * fallback, so an empty value is the Henaket default and nothing else
 * has to know about this. The focus ring color replaces --color-focus,
 * which every focus rule of the platform already uses, and the
 * visibility is an attribute global.css answers to.
 *
 * Loaded with /api/public/settings at App mount, and applied again by
 * the settings page on save, so the admin sees the change at once. */

export const BUTTON_VARIANTS = ['primary', 'secondary', 'tertiary'];
export const BUTTON_STATES = ['default', 'hover', 'active'];
export const BUTTON_PARTS = ['bg', 'border', 'text'];

/* What Button.module.css draws when nothing is set (Henaket v1.2.2). */
export const HENAKET_BUTTONS = {
  primary: {
    default_bg: '--color-blue-800', default_border: '--color-blue-600', default_text: '--color-white',
    hover_bg: '--color-blue-900', hover_border: '--color-blue-700', hover_text: '--color-white',
    active_bg: '--color-blue-1000', active_border: '--color-blue-800', active_text: '--color-white',
  },
  secondary: {
    default_bg: '--color-blue-200', default_border: '--color-blue-700', default_text: '--color-text-800',
    hover_bg: '--color-blue-300', hover_border: '--color-blue-800', hover_text: '--color-text-800',
    active_bg: '--color-blue-400', active_border: '--color-blue-900', active_text: '--color-text-800',
  },
  tertiary: {
    default_bg: '--color-white', default_border: '--color-text-600', default_text: '--color-text-800',
    hover_bg: '--color-text-200', hover_border: '--color-text-700', hover_text: '--color-text-800',
    active_bg: '--color-text-300', active_border: '--color-text-800', active_text: '--color-text-800',
  },
};
export const HENAKET_SIZES = { height: 40, height_large: 60, font_size: 16, radius: 4 };
export const HENAKET_FOCUS_HEX = '#BD13B8';

/* 0.46.83 - The Henaket dropdown (HenaketDropdown.module.css). */
export const HENAKET_DROPDOWN = {
  bg: '--color-white', border: '--color-grey-1200', text: '--color-text-800',
  panel_bg: '--color-white', panel_border: '--color-text-700', panel_text: '--color-text-800',
  hover_bg: '--color-text-200', hover_text: '--color-text-800',
  selected_bg: '--color-blue-800', selected_text: '--color-white',
};
export const HENAKET_DROPDOWN_SIZES = { height: 40, font_size: 16, radius: 4 };
/* 0.47.11 - The navigation menu's current-page marks (Header.module.css):
 * the underline under the current page on large screens and the left
 * mark in the small-screen menu. */
export const HENAKET_NAV = { active_color: '--color-blue-800', mobile_active_color: '--color-blue-800' };
export const HENAKET_NAV_SIZES = { active_height: 8 };

/* 0.47.27 - Fonts (Settings > Design system > Fonts).
 *
 * The two built-in choices, each an Armenian font with its Latin, and the
 * matching Noto font for Cyrillic (the Armenian fonts have none). The
 * default (Noto Sans) is loaded by main.jsx; any other font is loaded
 * here when it is used. An added font is followed by the default stack,
 * so letters it does not have (Armenian, Cyrillic) still show in Noto. */
export const FONT_PRESETS = {
  noto_sans: {
    label: 'Noto Sans Armenian, with Noto Sans for Cyrillic (Henaket)',
    stack: "'Noto Sans Armenian', 'Noto Sans', system-ui, sans-serif",
    css: '',
  },
  noto_serif: {
    label: 'Noto Serif Armenian, with Noto Serif for Cyrillic',
    stack: "'Noto Serif Armenian', 'Noto Serif', Georgia, serif",
    css: '',
    /* 0.49.61 - Served with the site (were Google Fonts), loaded only
       when chosen. */
    load: () => Promise.all([
      import('@fontsource/noto-serif-armenian/400.css'), import('@fontsource/noto-serif-armenian/500.css'),
      import('@fontsource/noto-serif-armenian/600.css'), import('@fontsource/noto-serif-armenian/700.css'),
      import('@fontsource/noto-serif/400.css'), import('@fontsource/noto-serif/500.css'),
      import('@fontsource/noto-serif/600.css'), import('@fontsource/noto-serif/700.css'),
    ]),
  },
};
const DEFAULT_STACK = FONT_PRESETS.noto_sans.stack;
const FONT_FILE_RE = /\.(woff2?|ttf|otf)(\?.*)?$/i;
const FONT_FORMAT = { woff2: 'woff2', woff: 'woff', ttf: 'truetype', otf: 'opentype' };

/* The CSS font stack for a font key ('' -> null, the default). */
export function fontStack(key, fonts) {
  if (!key) return null;
  if (FONT_PRESETS[key]) return FONT_PRESETS[key].stack;
  const f = ((fonts && fonts.custom) || []).find(c => c.id === key);
  return f ? `'${f.family}', ${DEFAULT_STACK}` : null;
}

/* Load the fonts these keys need: a stylesheet link for a preset or a
 * Google Fonts address, @font-face rules for a font file address or
 * uploaded files. Each is added once. */
const _presetLoaded = new Set();
export function ensureFontsLoaded(keys, fonts) {
  if (typeof document === 'undefined') return;
  const head = document.head;
  let style = document.getElementById('gt18-font-faces');
  for (const key of keys) {
    if (!key) continue;
    const preset = FONT_PRESETS[key];
    const f = preset ? null : ((fonts && fonts.custom) || []).find(c => c.id === key);
    if (preset && typeof preset.load === 'function') {
      if (!_presetLoaded.has(key)) { _presetLoaded.add(key); preset.load().catch(() => _presetLoaded.delete(key)); }
      continue;
    }
    const linkHref = preset ? preset.css : (f && f.url && !FONT_FILE_RE.test(f.url) ? f.url : '');
    if (linkHref) {
      const id = `gt18-font-${key}`;
      if (!document.getElementById(id)) {
        const l = document.createElement('link');
        l.id = id; l.rel = 'stylesheet'; l.href = linkHref;
        head.appendChild(l);
      }
      continue;
    }
    if (!f) continue;
    const srcs = f.files && f.files.length
      ? f.files.map(x => ({ url: x.url, weight: x.weight }))
      : (f.url ? [{ url: f.url, weight: 'variable' }] : []);
    for (const x of srcs) {
      const rule = `@font-face{font-family:'${f.family}';src:url("${x.url}")${(() => {
        const m = String(x.url).match(FONT_FILE_RE); const fmt = m && FONT_FORMAT[m[1].toLowerCase()];
        return fmt ? ` format("${fmt}")` : '';
      })()};font-weight:${x.weight === 'variable' ? '100 900' : x.weight};font-style:normal;font-display:swap;}`;
      if (!style) { style = document.createElement('style'); style.id = 'gt18-font-faces'; head.appendChild(style); }
      if (!style.textContent.includes(rule)) style.textContent += rule + '\n';
    }
  }
}
const DD_VARS = {
  bg: 'bg', border: 'border', text: 'text', panel_bg: 'panel-bg', panel_border: 'panel-border',
  panel_text: 'panel-text', hover_bg: 'hover-bg', hover_text: 'hover-text',
  selected_bg: 'selected-bg', selected_text: 'selected-text',
};
const DD_SIZE_VARS = { height: 'height', font_size: 'font-size', radius: 'radius' };

function css(value) {
  if (!value || typeof value !== 'string') return '';
  // --color-focus itself would point the ring at itself.
  if (/^--color-[a-z0-9-]+$/.test(value) && value !== '--color-focus') return `var(${value})`;
  if (/^#[0-9A-Fa-f]{6}$/.test(value)) return value;
  return '';
}

const SIZE_VARS = { height: 'height', height_large: 'height-lg', font_size: 'font-size', radius: 'radius' };

export function applyUiTheme(cfg, { store = false } = {}) {
  if (typeof document === 'undefined') return;
  if (store) storeUiTheme(cfg);
  const root = document.documentElement;
  const set = (name, value) => {
    if (value) root.style.setProperty(name, value);
    else root.style.removeProperty(name);
  };
  const btns = (cfg && cfg.buttons) || {};
  for (const v of BUTTON_VARIANTS) {
    const row = btns[v] || {};
    for (const s of BUTTON_STATES) {
      for (const p of BUTTON_PARTS) {
        set(`--btn-${v}-${s}-${p}`, css(row[`${s}_${p}`]));
      }
    }
    for (const [k, name] of Object.entries(SIZE_VARS)) {
      const n = Number(row[k]);
      set(`--btn-${v}-${name}`, row[k] != null && row[k] !== '' && Number.isFinite(n) ? `${n}px` : '');
    }
  }
  const dd = (cfg && cfg.dropdown) || {};
  for (const [k, name] of Object.entries(DD_VARS)) set(`--dd-${name}`, css(dd[k]));
  for (const [k, name] of Object.entries(DD_SIZE_VARS)) {
    const n = Number(dd[k]);
    set(`--dd-${name}`, dd[k] != null && dd[k] !== '' && Number.isFinite(n) ? `${n}px` : '');
  }
  // 0.47.27 - Fonts: the text font and the headings' font.
  const fonts = (cfg && cfg.fonts) || {};
  ensureFontsLoaded([fonts.text, fonts.headings], fonts);
  set('--font-family-base', fontStack(fonts.text, fonts) || '');
  set('--font-family-headings', fontStack(fonts.headings, fonts) || '');
  const nav = (cfg && cfg.nav) || {};
  set('--nav-active-color', css(nav.active_color));
  set('--nav-mobile-active-color', css(nav.mobile_active_color));
  {
    const n = Number(nav.active_height);
    set('--nav-active-height', nav.active_height != null && nav.active_height !== '' && Number.isFinite(n) ? `${n}px` : '');
  }
  const focus = (cfg && cfg.focus) || {};
  set('--color-focus', css(focus.color));
  if (focus.visibility === 'no_click') root.setAttribute('data-focus-ring', 'no-click');
  else root.removeAttribute('data-focus-ring');
  watchInput();
}

/* 0.46.81 - "Do not show on click": the page notes whether the last
 * thing the visitor did was a click or tap (pointer) or a key, as
 * data-input on the page root. global.css hides every focus and press
 * ring while it is "pointer" and the setting asks for that, and shows
 * them again at the first key. Installed once. */
let watching = false;
function watchInput() {
  if (watching || typeof document === 'undefined') return;
  watching = true;
  const root = document.documentElement;
  document.addEventListener('pointerdown', () => root.setAttribute('data-input', 'pointer'), true);
  document.addEventListener('keydown', (e) => {
    if (e.metaKey || e.altKey || e.ctrlKey) return;
    root.setAttribute('data-input', 'keyboard');
  }, true);
}

/* 0.46.78 - The last theme this browser saw, applied before the first
 * paint (main.jsx), so buttons do not change color when
 * /api/public/settings answers. The fetch still applies and stores the
 * current one. */
const CACHE_KEY = 'gt18.uiTheme.v1';

export function applyCachedUiTheme() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (raw) applyUiTheme(JSON.parse(raw));
  } catch {
    /* no cache: Henaket until the settings arrive */
  }
}

export function storeUiTheme(cfg) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cfg || {}));
  } catch {
    /* storage blocked: nothing to do */
  }
}
